import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';

@Component({
  /* eslint-disable @angular-eslint/component-selector */
  selector: '[app-sub-menu]',
  templateUrl: './sub-menu.component.html',
  styleUrl: './sub-menu.component.scss',
  animations: [
    trigger('children', [
      state(
        'hiddenAnimated',
        style({
          height: '0px',
        }),
      ),
      state(
        'visibleAnimated',
        style({
          height: '*',
        }),
      ),
      state(
        'visible',
        style({
          height: '*',
          'z-index': 100,
        }),
      ),
      state(
        'hidden',
        style({
          height: '0px',
          'z-index': '*',
        }),
      ),
      transition(
        'visibleAnimated => hiddenAnimated',
        animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'),
      ),
      transition(
        'hiddenAnimated => visibleAnimated',
        animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'),
      ),
    ]),
  ],
  standalone: true,
  imports: [CommonModule, RouterModule, SubMenuComponent],
})
export class SubMenuComponent implements OnChanges {
  @Input() item?: MenuItem | MenuItem[];

  @Input() root: boolean = false;

  @Input() visible?: boolean;

  @Input() activeUrl?: string;

  @Output() clickMenu = new EventEmitter();
  @Output() resetMenu = new EventEmitter();

  @Output() moveBar = new EventEmitter();

  _reset = false;

  _parentActive = false;

  activeIndex?: number | null;

  menuHoverActive = false;

  menuItens: MenuItem[] = [];

  get displayItems(): MenuItem[] {
    if (!this.item) return [];
    return this.root
      ? (this.item as MenuItem[])
      : ((this.item as MenuItem).items ?? []);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeUrl'] || changes['item'] || changes['root']) {
      if (this.activeUrl) {
        this.updateActiveIndex(this.activeUrl);
      }
    }
  }

  private updateActiveIndex(url: string): void {
    if (!this.item) return;

    // `root` determina o formato de `item`: lista completa no nível raiz,
    // ou um único MenuItem cujos filhos (`items`) são percorridos abaixo.
    const items: MenuItem[] = this.root
      ? (this.item as MenuItem[])
      : ((this.item as MenuItem).items ?? []);

    const matchedIndex = items.findIndex((child: MenuItem) =>
      this.itemMatchesUrl(child, url),
    );

    if (matchedIndex !== -1) {
      this.activeIndex = matchedIndex;
    }
  }

  private itemMatchesUrl(item: MenuItem, url: string): boolean {
    if (item.routerLink) {
      const link = Array.isArray(item.routerLink)
        ? item.routerLink.join('/')
        : item.routerLink;
      const normalised = ('/' + link).replace(/\/+/g, '/');

      if (normalised === '/') {
        return url === '/' || url === '';
      }

      // Match EXATO pra items leaf (routerLink) — só ativa se URL
      // bate o exato path OU é sub-rota de edição/criação do próprio
      // item.
      //
      // Bug #1 (fix baef4128): `startsWith(normalised + '/')` solto
      // fazia "Produto" (/cadastros/produtos) casar com SIBLINGS
      // como "Cor" (/cadastros/produtos/cores) — é sibling no menu,
      // não sub-item.
      //
      // Bug #2 (fix ATUAL): em `/cadastros/produtos/cores/5`, o item
      // "Operador" (/cadastros/pdv/operadores — MESMO tamanho 25 do
      // routerLink de Cor por coincidência) casava porque slice blind
      // não verificava prefix. url.slice(25) = '/5' + regex \d+ →
      // TRUE. Menu marcava PDV→Operador em rota de Cor.
      //
      // Fix: `startsWith(normalised + '/')` ANTES de inspecionar
      // nextSegment. Evita match acidental entre rotas unrelated
      // de comprimento igual.
      if (url === normalised) return true;
      if (!url.startsWith(normalised + '/')) return false;
      // afterPrefix começa com '/' por construção. Próximo segmento
      // deve ser ID numérico ou palavra-chave CRUD — não nome de
      // sibling. /novo, /nova, /123, /edit, /editar.
      const nextSegment = url.slice(normalised.length + 1).split('/')[0];
      return /^(\d+|novo|nova|edit|editar)$/i.test(nextSegment);
    }
    if (item.items) {
      return item.items.some((child: MenuItem) =>
        this.itemMatchesUrl(child, url),
      );
    }
    return false;
  }

  itemClick(event: Event, item: MenuItem, index: number): void {
    if (this.root) {
      this.menuHoverActive = !this.menuHoverActive;
      this.clickMenu.emit({ menuHoverActive: this.menuHoverActive });
    }

    // avoid processing disabled items
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    if (item.items) {
      this.activeIndex = this.activeIndex === index ? null : index;
    } else {
      this.activeIndex = index;
    }
    if (item.command) {
      item.command({ originalEvent: event, item: item });
    }

    // prevent hash change
    if (item.items || (!item.url && !item.routerLink)) {
      setTimeout(() => {
        this.moveBar.emit({ menuHoverActive: this.menuHoverActive });
      }, 450);
      event.preventDefault();
    }
  }

  onMouseEnter(_index: number) {}

  isActive(index: number): boolean {
    return this.activeIndex === index;
  }

  @Input() get reset(): boolean {
    return this._reset;
  }

  set reset(val: boolean) {
    this._reset = val;
  }

  @Input() get parentActive(): boolean {
    return this._parentActive;
  }

  set parentActive(val: boolean) {
    this._parentActive = val;

    if (!this._parentActive) {
      this.activeIndex = null;
    }
  }
}
